import React from 'react';
import {BIOMARKER_STATUS_COLORS, BIOMARKER_INDICATOR_COLOR} from '@/shared/constants/colors';

interface ProgressBarSection {
    width: number; // percentage (0-100)
    color: string;
}

interface ProgressBarProps {
    sections?: ProgressBarSection[];
    indicatorPosition?: number; // percentage (0-100)
    indicatorColor?: string;
    showIndicator?: boolean;
    className?: string;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
    sections = [
        {width: 33.33, color: BIOMARKER_STATUS_COLORS.LOW},
        {width: 33.33, color: BIOMARKER_STATUS_COLORS.NORMAL},
        {width: 33.33, color: BIOMARKER_STATUS_COLORS.HIGH},
    ],
    indicatorPosition = 50,
    indicatorColor,
    showIndicator = true,
    className = '',
}) => {
    // Ensure sections add up to 100%
    const totalWidth = sections.reduce((sum, section) => sum + section.width, 0);
    const normalizedSections = sections.map((section) => ({
        ...section,
        width: (section.width / totalWidth) * 100,
    }));

    // Clamp indicator position to prevent clipping outside the bars
    const clampedPosition = Math.max(2, Math.min(indicatorPosition, 98));

    // The vertical indicator is status-agnostic — always the design-system info
    // blue (BIOMARKER_INDICATOR_COLOR), regardless of which zone the value falls
    // in. Only the reference-range zones (`sections`) stay status-colored.
    // Centralizing it here keeps every ProgressBar consumer (the dashboard Lab
    // Results card AND the /results BiomarkerChartCard) consistent without each
    // call site repeating the color. Callers may still override via `indicatorColor`.
    // (This also supersedes the old at-boundary zone-color fix — the indicator
    // is never status-colored now, so a "Normal" value can't show a red slider.)
    const getIndicatorColor = () => indicatorColor || BIOMARKER_INDICATOR_COLOR;

    return (
        <div className={`rounded-2xl p-3 mb-4 relative bg-mld-background ${className}`}>
            <div className="relative h-3 flex gap-1">
                {normalizedSections.map((section, index) => (
                    <div
                        key={index}
                        className="h-3 rounded-full"
                        style={{
                            width: `${section.width}%`,
                            backgroundColor: section.color,
                        }}
                    />
                ))}
            </div>

            {showIndicator && (
                <div
                    className="absolute rounded-full z-10"
                    style={{
                        left: `${clampedPosition}%`,
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '9px',
                        height: '50px',
                        backgroundColor: getIndicatorColor(),
                        outline: '3px solid rgb(var(--mld-menu-background))',
                    }}
                />
            )}
        </div>
    );
};

export default ProgressBar;
